import { forwardRef, memo } from 'react';
import { styled } from '@mui/material/styles';
import { Box, SxProps } from '@mui/material';
import linkifyHtml from 'linkify-html';
import { highlightSearchKeyword } from 'utility/helpers';
import { ArticleVariantType } from '@buildwise/pages/knowledge/components/ArticleBox';
import { decode } from 'html-entities';

type PostDescriptionProps = {
  description: string;
  isDetailedView: boolean;
  isRelatedPostView: boolean;
  searchKeyword?: string;
  sx?: SxProps;
  variant: ArticleVariantType;
};

const PostDescription = forwardRef<HTMLParagraphElement, PostDescriptionProps>(
  (
    { description, isDetailedView, isRelatedPostView, searchKeyword = '', sx, variant },
    articleDescriptionRef,
  ) => {
    if (description) {
      return (
        <ArticleDescription
          ref={isRelatedPostView ? null : articleDescriptionRef}
          className={variant}
          dangerouslySetInnerHTML={{
            __html: linkifyHtml(
              decode(
                searchKeyword ? highlightSearchKeyword(description, searchKeyword) : description,
              ),
              { target: '_blank' },
            ),
          }}
          sx={{
            ...sx,
            ...(!isDetailedView && {
              '& img, & table': {
                display: 'none',
              },
            }),
          }}
        />
      );
    }

    return null;
  },
);

export const ArticleDescription = styled(Box)(({ theme }) => ({
  position: 'relative',
  paddingTop: 8,
  wordBreak: 'break-word',
  fontFamily: 'Lato, sans-serif',
  fontSize: 16,
  color: theme.palette.custom.black,
  '& p': {
    margin: '0 0 8px',
  },
  '& *': {
    fontFamily: 'Lato, sans-serif !important',
    maxWidth: '100% !important',
  },
  '&.short, &.list, &.draft, &.related-post': {
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: '2',
    WebkitBoxOrient: 'vertical',
    // maxHeight: 150, // It's causing issues in rendering in Safari browser. So, hiding it for now. BWA-924
    textOverflow: 'ellipsis',
  },
  '&.short *, &.list *, &.draft *, &.related-post *': {
    // maxHeight: 100,
  },
  '& .highlight': {
    backgroundColor: `${theme.palette.custom.paleGold} !important`,
  },
  '& a': {
    whiteSpace: 'pre-wrap',
    color: `${theme.palette.custom.link} !important`,
    '&:hover': {
      textDecoration: 'underline !important',
    },
  },
  '&.related-post': {
    lineHeight: '20px',
    paddingTop: 4,
    fontSize: 14,
  },
  '& span.mention, & span.hashtag': {
    backgroundColor: theme.palette.custom.aliceBlue,
    color: theme.palette.custom.link,
    borderRadius: 4,
    padding: 2,
  },
  '& span.mention': {
    marginRight: 4,
  },
  '& span.hashtag:hover': {
    textDecoration: 'underline',
    cursor: 'pointer',
  },
}));

export default memo(PostDescription);
